import type { ReactNode } from 'react';

import { EmptyState } from '../../../chrome/state';
import { cellText, DataTable, SpecChart } from '../../../chrome/chart/SpecChart';
import type { ProvenanceStripProps } from '../shared/ProvenanceStrip';
import { labelValue, labelVocabularyValue } from '../publication/compute';
import { SentimentCard } from '../sentiment/SentimentCard';
import { at } from './messages';
import type { ChartResult, QueryResult } from './schemas';

/** A category on an axis, a series or a slice, named as the composition card names it: a
 *  vocabulary value by its label under the column's dimension, a missing one as unidentified. */
function category(column: string, value: unknown): string {
  if (value === null || value === undefined) return at('card.composition.unidentified');
  if (typeof value === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return labelVocabularyValue(value) ?? labelValue(column, value);
  }
  return cellText(value);
}

export function ChartCard(props: {
  result: ChartResult;
  provenance?: ProvenanceStripProps;
}): ReactNode {
  const { chart, rows } = props.result;
  let body: ReactNode;
  if (rows.length === 0) {
    body = <EmptyState title={at('card.chart.empty')} />;
  } else if (chart.kind === 'table') {
    body = (
      <DataTable
        rows={rows}
        columns={[
          chart.x,
          ...(chart.series === null ? [] : [chart.series]),
          ...chart.y,
          ...chart.y2,
        ]}
      />
    );
  } else {
    body = (
      <SpecChart
        spec={chart}
        rows={rows}
        category={category}
        ariaLabel={chart.title ?? at('card.chart.title')}
      />
    );
  }
  return (
    <SentimentCard title={chart.title ?? at('card.chart.title')} provenance={props.provenance}>
      {body}
      <p className="text-caption text-muted-foreground">{source(props.result.provenance)}</p>
      {props.result.provenance.sql != null && (
        <pre className="overflow-x-auto rounded-md border border-border bg-muted p-3 font-mono text-mono-sm whitespace-pre-wrap">
          {props.result.provenance.sql}
        </pre>
      )}
    </SentimentCard>
  );
}

/** Which tool read the plotted rows and the days it read, so a chart is never taken for a
 *  figure no tool returned. */
function source(provenance: ChartResult['provenance']): string {
  // Free SQL proves nothing about where its values came from, so it claims no source.
  if (provenance.tool === 'run_query') return at('card.chart.sourceSql');
  const tool = at('tool.get_counts');
  const from = provenance.window?.from ?? null;
  const to = provenance.window?.to ?? null;
  if (from === null && to === null) return at('card.chart.source', { tool });
  const window = [from, to]
    .filter((day): day is string => day !== null)
    .map((day) => cellText(day))
    .join(' – ');
  return at('card.chart.sourceWindow', { tool, window });
}

export function QueryCard(props: { result: QueryResult }): ReactNode {
  const { rows, truncated } = props.result;
  return (
    <SentimentCard title={at('card.query.title')}>
      {rows.length === 0 ? (
        <EmptyState title={at('card.query.empty')} />
      ) : (
        <DataTable rows={rows} />
      )}
      {truncated && (
        <p className="text-caption text-muted-foreground">
          {at('card.query.truncated', { count: String(rows.length) })}
        </p>
      )}
    </SentimentCard>
  );
}
